/**
 * Enhanced Forecast Copy Helper with Configuration-Based Approach
 *
 * This file provides reusable utilities for copying metrics across months
 * in forecast components, with support for component-specific configurations.
 */

export interface MonthlyDataEntry {
  [key: string]: any;
}

export type CopyType = "month" | "quarter" | "year";

// ============================================================================
// COMPONENT CONFIGURATIONS
// ============================================================================

export interface EditableFieldConfig {
  metricKey: string;
  stateGetter: string; // Name of the state variable
}

export interface ComponentConfig {
  name: string;
  editableFields: Record<string, EditableFieldConfig>;
}

/**
 * Configuration for NewForecastForFTD component
 */
export const FTD_CONFIG: ComponentConfig = {
  name: "FTD",
  editableFields: {
    editableNRPU: { metricKey: "nrpu", stateGetter: "editableNRPU" },
    editableGrowthTarget: {
      metricKey: "growth",
      stateGetter: "editableGrowthTarget",
    },
    editableAvgNetREv: {
      metricKey: "avgNetRevPerActiveUser",
      stateGetter: "editableAvgNetREv",
    },
    editableCPAPV: { metricKey: "cpaPv", stateGetter: "editableCPAPV" },
    editableCVR: { metricKey: "cvr", stateGetter: "editableCVR" },
    editableProfit: { metricKey: "profit", stateGetter: "editableProfit" },
    editableChurnRate: {
      metricKey: "churnRate",
      stateGetter: "editableChurnRate",
    },
    editableAcquiredToFtd: {
      metricKey: "ratioBetweenAcquiredToFtd",
      stateGetter: "editableAcquiredToFtd",
    },
    editableRsP: { metricKey: "avgRsP", stateGetter: "editableRsP" },
    editableRsBrNg: {
      metricKey: "rsBrToRsActiveRatio",
      stateGetter: "editableRsBrNg",
    },
  },
};

/**
 * Configuration for NewForecastForProfit component
 */
export const PROFIT_CONFIG: ComponentConfig = {
  name: "Profit",
  editableFields: {
    editableNRPU: { metricKey: "nrpu", stateGetter: "editableNRPU" },
    editableFtd: { metricKey: "ftd", stateGetter: "editableFtd" },
    editableAvgNetREv: {
      metricKey: "avgNetRevPerActiveUser",
      stateGetter: "editableAvgNetREv",
    },
    editableCPAPV: { metricKey: "cpaPv", stateGetter: "editableCPAPV" },
    editableCVR: { metricKey: "cvr", stateGetter: "editableCVR" },
    editableCAC: { metricKey: "cac", stateGetter: "editableCAC" },
    editableChurnRate: {
      metricKey: "churnRate",
      stateGetter: "editableChurnRate",
    },
    editableAcquiredToFtd: {
      metricKey: "ratioBetweenAcquiredToFtd",
      stateGetter: "editableAcquiredToFtd",
    },
    editableRsP: { metricKey: "avgRsP", stateGetter: "editableRsP" },
    editableRsBrNg: {
      metricKey: "rsBrToRsActiveRatio",
      stateGetter: "editableRsBrNg",
    },
  },
};

/**
 * Configuration for NewForecastForGrowth component
 */
export const GROWTH_CONFIG: ComponentConfig = {
  name: "Growth",
  editableFields: {
    editableNRPU: { metricKey: "nrpu", stateGetter: "editableNRPU" },
    editableFtd: { metricKey: "ftd", stateGetter: "editableFtd" },
    editableAvgNetREv: {
      metricKey: "avgNetRevPerActiveUser",
      stateGetter: "editableAvgNetREv",
    },
    editableCPAPV: { metricKey: "cpaPv", stateGetter: "editableCPAPV" },
    editableCVR: { metricKey: "cvr", stateGetter: "editableCVR" },
    editableProfit: { metricKey: "profit", stateGetter: "editableProfit" },
    editableChurnRate: {
      metricKey: "churnRate",
      stateGetter: "editableChurnRate",
    },
    editableAcquiredToFtd: {
      metricKey: "ratioBetweenAcquiredToFtd",
      stateGetter: "editableAcquiredToFtd",
    },
    editableRsP: { metricKey: "avgRsP", stateGetter: "editableRsP" },
    editableRsBrNg: {
      metricKey: "rsBrToRsActiveRatio",
      stateGetter: "editableRsBrNg",
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS (Reusable across all components)
// ============================================================================

/**
 * Get month index from month name
 */
export const getMonthIndex = (monthName: string): number => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months.indexOf(monthName);
};

/**
 * Get month name from index
 */
export const getMonthName = (index: number): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[index] || "";
};

/**
 * Get target months based on copy type
 */
export const getTargetMonths = (
  sourceMonth: string,
  copyType: CopyType,
): string[] => {
  const sourceIndex = getMonthIndex(sourceMonth);
  if (sourceIndex === -1) return [];

  const targetMonths: string[] = [];

  switch (copyType) {
    case "month":
      // Copy to next month only
      if (sourceIndex < 11) {
        targetMonths.push(getMonthName(sourceIndex + 1));
      }
      break;

    case "quarter":
      // Copy to all months until end of quarter
      // Q1: 0-2 (Jan-Mar), Q2: 3-5 (Apr-Jun), Q3: 6-8 (Jul-Sep), Q4: 9-11 (Oct-Dec)
      const quarterEndMonth = Math.floor(sourceIndex / 3) * 3 + 2;
      for (let i = sourceIndex + 1; i <= quarterEndMonth && i <= 11; i++) {
        targetMonths.push(getMonthName(i));
      }
      break;

    case "year":
      // Copy to all months until end of year
      for (let i = sourceIndex + 1; i <= 11; i++) {
        targetMonths.push(getMonthName(i));
      }
      break;

    default:
      break;
  }

  return targetMonths;
};

/**
 * Copy a metric value from source month to target months
 */
export const copyMetricToMonths = (
  monthlyData: Record<string, MonthlyDataEntry>,
  sourceMonth: string,
  metricKey: string,
  copyType: CopyType,
): Record<string, MonthlyDataEntry> => {
  const sourceData = monthlyData[sourceMonth];
  if (!sourceData || sourceData[metricKey] === undefined) {
    console.warn(`No data found for ${sourceMonth}.${metricKey}`);
    return monthlyData;
  }

  const valueToUpdate = sourceData[metricKey];
  const targetMonths = getTargetMonths(sourceMonth, copyType);

  const updatedData = { ...monthlyData };

  targetMonths.forEach((targetMonth) => {
    if (updatedData[targetMonth]) {
      updatedData[targetMonth] = {
        ...updatedData[targetMonth],
        [metricKey]: valueToUpdate,
      };
    }
  });

  return updatedData;
};

// ============================================================================
// GENERIC FUNCTIONS (Configuration-based)
// ============================================================================

/**
 * Generic function to get current editing metric based on configuration
 *
 * @param editableStates - Object containing all editable state values
 * @param config - Component configuration
 * @returns Object with month and metricKey, or null if no field is being edited
 */
export const getCurrentMetricKeyGeneric = (
  editableStates: Record<string, string | null>,
  config: ComponentConfig,
): { month: string; metricKey: string } | null => {
  // Iterate through the configuration to find which field is being edited
  for (const fieldConfig of Object.values(config.editableFields)) {
    const stateValue = editableStates[fieldConfig.stateGetter];
    if (stateValue) {
      return { month: stateValue, metricKey: fieldConfig.metricKey };
    }
  }

  console.warn(`No editable field found for ${config.name} component`);
  return null;
};

/**
 * Generic copy handler function
 *
 * This function can be used by any forecast component to handle copy operations.
 * It uses the component configuration to determine which metric is being edited
 * and performs the copy operation accordingly.
 *
 * @param copyType - Type of copy operation (month, quarter, year)
 * @param editableStates - Object containing all editable state values
 * @param monthlyData - Current monthly data from Redux
 * @param config - Component configuration
 * @param monthNames - Array of month names
 * @param calculateValues - Function to recalculate derived values
 * @param dispatch - Redux dispatch function
 * @param setMonthlyDataAction - Redux action creator for setting monthly data
 */
export const handleCopyToNextGeneric = (
  copyType: CopyType,
  editableStates: Record<string, string | null>,
  monthlyData: Record<string, MonthlyDataEntry>,
  config: ComponentConfig,
  monthNames: string[],
  calculateValues: (month: string, data: any, prevActiveUsers?: number) => any,
  dispatch: any,
  setMonthlyDataAction: any,
) => {
  // Get current metric info based on configuration
  const currentMetric = getCurrentMetricKeyGeneric(editableStates, config);

  if (!currentMetric) {
    console.error(`No metric is currently being edited in ${config.name}`);
    return;
  }

  const { month: sourceMonth, metricKey } = currentMetric;

  // Copy the metric to target months
  const updatedMonthlyData = copyMetricToMonths(
    monthlyData,
    sourceMonth,
    metricKey,
    copyType,
  );

  // Recalculate all affected months
  const sourceMonthIndex = monthNames.indexOf(sourceMonth);

  for (let i = sourceMonthIndex; i < monthNames.length; i++) {
    const currentMonth = monthNames[i];
    if (updatedMonthlyData[currentMonth]) {
      if (i === sourceMonthIndex) {
        // For the source month, recalculate with existing values
        updatedMonthlyData[currentMonth] = calculateValues(
          currentMonth,
          updatedMonthlyData[currentMonth],
        );
      } else {
        // For subsequent months, recalculate with previous month's active users
        const prevMonth = monthNames[i - 1];
        const prevMonthActiveUsers = updatedMonthlyData[prevMonth].activeUsers;
        updatedMonthlyData[currentMonth] = calculateValues(
          currentMonth,
          updatedMonthlyData[currentMonth],
          prevMonthActiveUsers,
        );
      }
    }
  }

  // Update the Redux store
  dispatch(setMonthlyDataAction(updatedMonthlyData));
};

// ============================================================================
// LEGACY COMPATIBILITY (Deprecated - Use Generic Functions Instead)
// ============================================================================

/**
 * @deprecated Use getCurrentMetricKeyGeneric with FTD_CONFIG instead
 *
 * Legacy function for FTD component compatibility.
 * This function should be replaced with the generic version.
 */
export const getCurrentMetricKey = (
  editableNRPU: string | null,
  editableGrowthTarget: string | null,
  editableAvgNetREv: string | null,
  editableCPAPV: string | null,
  editableCVR: string | null,
  editableProfit: string | null,
  editableChurnRate: string | null,
  editableAcquiredToFtd: string | null,
  editableRsP: string | null,
  editableRsBrNg: string | null,
): { month: string; metricKey: string } | null => {
  const editableStates = {
    editableNRPU,
    editableGrowthTarget,
    editableAvgNetREv,
    editableCPAPV,
    editableCVR,
    editableProfit,
    editableChurnRate,
    editableAcquiredToFtd,
    editableRsP,
    editableRsBrNg,
  };

  return getCurrentMetricKeyGeneric(editableStates, FTD_CONFIG);
};
